import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Plus, Search } from "lucide-react";
import {
  KbSearchResult,
  fetchKbHome,
  fetchKbMyDrafts,
  searchKb,
} from "@/api/knowledge";
import {
  KbDraftChip,
  KbErrorBanner,
  KbErrorCard,
  KbRow,
  KbRowsSkeleton,
  KbSecTitle,
  KbTilesSkeleton,
  kbPublishedRight,
  kbTypeSuffix,
} from "./kbUi";

/**
 * D-147 W1 S3+S4 — «База знань» Home (visual package v1.2 LOCK, екран ①).
 *
 * Reader (S3): пошук published-scope · плитки з publishedCount · «Нещодавно
 * опубліковане». Editor+ (S4): draftCount-суфікс плитки · «Мої чернетки»
 * (предикат A5 — server-side, my-drafts) · toggle «Шукати також у чернетках…»
 * (scope=drafts, server-side reject для reader; результати марковані
 * «чернетка»). Видимість editor-поверхонь = наявність editor+-capability у
 * backend-плитках (нуль FE-інференсів понад payload).
 */
export function KnowledgeHomePage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeDrafts, setIncludeDrafts] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const home = useQuery({
    queryKey: ["kbHome"],
    queryFn: ({ signal }) => fetchKbHome(signal),
    staleTime: 30_000,
  });

  const editorGrade = (home.data?.sections ?? []).some((s) =>
    ["editor", "owner", "admin"].includes(s.capability),
  );

  const myDrafts = useQuery({
    queryKey: ["kbMyDrafts"],
    queryFn: ({ signal }) => fetchKbMyDrafts(signal),
    enabled: editorGrade,
    staleTime: 30_000,
  });

  const searching = search.length >= 2;
  const searchQ = useQuery({
    queryKey: ["kbSearch", search],
    queryFn: ({ signal }) => searchKb(search, "published", signal),
    enabled: searching,
    staleTime: 15_000,
  });
  const searchDraftsQ = useQuery({
    queryKey: ["kbSearchDrafts", search],
    queryFn: ({ signal }) => searchKb(search, "drafts", signal),
    enabled: searching && includeDrafts && editorGrade,
    staleTime: 15_000,
  });

  const sections = home.data?.sections ?? [];
  const recent = home.data?.recent ?? [];
  const draftRows = myDrafts.data?.items ?? [];

  const searchResults: KbSearchResult[] = [
    ...(searchQ.data?.results ?? []),
    ...(includeDrafts && editorGrade ? (searchDraftsQ.data?.results ?? []) : []),
  ];

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1080px]">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <BookOpen className="h-5 w-5" aria-hidden />
            </div>
            <h1 className="text-xl font-semibold tracking-tight">База знань</h1>
          </div>
          <label className="flex min-w-[220px] max-w-[520px] flex-1 items-center gap-2 rounded-lg border bg-background px-3 py-2 focus-within:ring-1 focus-within:ring-ring">
            <Search className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Пошук по назві та тексту матеріалів…"
              aria-label="Пошук по назві та тексту матеріалів"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </label>
          {home.data?.homeActions.createSection && (
            <Link
              to="/knowledge/admin"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
            >
              <Plus className="h-4 w-4" aria-hidden /> Створити розділ
            </Link>
          )}
        </div>

        {editorGrade && (
          <label className="mb-3 flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeDrafts}
              onChange={(e) => setIncludeDrafts(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary"
            />
            Шукати також у чернетках доступних мені розділів
          </label>
        )}

        {searching ? (
          <SearchResults
            q={search}
            isPending={searchQ.isPending || (includeDrafts && editorGrade && searchDraftsQ.isPending)}
            isError={searchQ.isError}
            refetch={() => {
              void searchQ.refetch();
              if (includeDrafts && editorGrade) void searchDraftsQ.refetch();
            }}
            results={searchResults}
            hasData={searchQ.data != null}
          />
        ) : home.isPending ? (
          <>
            <KbSecTitle>Розділи</KbSecTitle>
            <KbTilesSkeleton />
            <KbSecTitle>Нещодавно опубліковане</KbSecTitle>
            <KbRowsSkeleton />
          </>
        ) : home.isError && !home.data ? (
          <KbErrorCard text="Не вдалося завантажити базу знань" onRetry={() => void home.refetch()} />
        ) : (
          <>
            {home.isError && (
              <KbErrorBanner
                text="Не вдалося завантажити базу знань — показано попередні дані."
                onRetry={() => void home.refetch()}
              />
            )}
            {sections.length === 0 ? (
              <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
                <div className="text-sm font-medium">У базі знань поки немає розділів, доступних вам</div>
              </div>
            ) : (
              <>
                <KbSecTitle>Розділи</KbSecTitle>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {sections.map((s) => (
                    <Link
                      key={s.id}
                      to={`/knowledge/sections/${s.id}`}
                      className="rounded-xl border bg-card p-4 transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-none"
                    >
                      <h3 className="text-sm font-bold">{s.name}</h3>
                      {s.description && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.description}</p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{s.owner ? `Власник: ${s.owner.name}` : ""}</span>
                        <span className="flex-none">
                          {s.publishedCount} опублікованих
                          {s.draftCount != null && s.draftCount > 0 && (
                            <span className="text-warning-foreground"> · {s.draftCount} чернетки</span>
                          )}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>

                {recent.length > 0 && (
                  <>
                    <KbSecTitle>Нещодавно опубліковане</KbSecTitle>
                    <div className="overflow-hidden rounded-xl border bg-card">
                      {recent.map((r) => (
                        <KbRow
                          key={r.itemId}
                          to={`/knowledge/items/${r.itemId}`}
                          type={r.type}
                          title={r.title}
                          sub={`${r.sectionName} · ${kbTypeSuffix(r.type, r.originalFilename, r.fileSize)}`}
                          right={kbPublishedRight(r.lastPublishedAt)}
                        />
                      ))}
                    </div>
                  </>
                )}

                {editorGrade && draftRows.length > 0 && (
                  <>
                    <KbSecTitle>Мої чернетки</KbSecTitle>
                    <div className="overflow-hidden rounded-xl border bg-card">
                      {draftRows.map((d) => (
                        <KbRow
                          key={d.itemId}
                          to={`/knowledge/items/${d.itemId}?view=draft`}
                          type={d.type}
                          title={d.draftTitle || d.publishedTitle}
                          sub={`${d.sectionName} · відповідальний: ви`}
                          right={d.draftModifiedAt ? `змінено ${formatShortDate(d.draftModifiedAt)}` : undefined}
                          chip={<KbDraftChip>чернетка</KbDraftChip>}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SearchResults({
  q,
  isPending,
  isError,
  refetch,
  results,
  hasData,
}: {
  q: string;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  results: KbSearchResult[];
  hasData: boolean;
}) {
  if (isPending) {
    return (
      <>
        <KbSecTitle>Результати пошуку</KbSecTitle>
        <KbRowsSkeleton />
      </>
    );
  }
  if (isError && !hasData) {
    return <KbErrorCard text="Не вдалося виконати пошук" onRetry={refetch} />;
  }
  if (results.length === 0) {
    // filtered-empty — тексти дослівно (visual package, екран ①)
    return (
      <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
        <div className="text-sm font-medium">Нічого не знайдено за «{q}».</div>
        <div className="mt-1 text-sm text-muted-foreground">Спробуйте інше формулювання.</div>
      </div>
    );
  }
  return (
    <>
      <KbSecTitle>Результати пошуку</KbSecTitle>
      <div className="overflow-hidden rounded-xl border bg-card">
        {results.map((r) => (
          <KbRow
            key={`${r.scope}-${r.itemId}`}
            to={`/knowledge/items/${r.itemId}${r.scope === "draft" ? "?view=draft" : ""}`}
            type={r.type}
            title={r.title}
            sub={[`${r.sectionName} · ${r.type === "page" ? "сторінка" : "файл"}`, r.snippet]
              .filter(Boolean)
              .join(" · ")}
            chip={r.scope === "draft" ? <KbDraftChip>чернетка</KbDraftChip> : undefined}
          />
        ))}
      </div>
    </>
  );
}

function formatShortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
}
